// Auto-generated CRM User Manual Content
// Each page: summary, howTo, detailed breakdown with formulas + data diagrams, relatedPages

const MANUAL = {
  '/': {
    title: 'Dashboard',
    summary: 'The Dashboard is your CRM home screen. It provides a real-time snapshot of business activity including client count, active jobs, open leads, pipeline value, recent appraisals, and job status distribution.',
    howTo: [
      'View key metrics at a glance in the KPI cards at the top.',
      'The pipeline breakdown shows how many appraisals are at each status stage.',
      'Recent appraisals list the 10 most recently created jobs with client names and statuses.',
      'All data respects the CRM Start Date setting — data before that date is excluded.',
    ],
    detailed: `DATA SOURCES:
\u2022 Clients Count \u2192 clients collection (total documents)
\u2022 Appraisals Count \u2192 appraisals collection (filtered by CRM Start Date on appraisal_date)
\u2022 Active Jobs \u2192 appraisals where status \u2208 {Quoted, Accepted, Work In Progress, Work Booked}
\u2022 Open Leads \u2192 leads where lead_closed \u2260 true
\u2022 Total Contract Value \u2192 SUM(appraisals.contract_amount)

PIPELINE STATUS FLOW:
Appraisal Required \u2192 Appraisal Booked \u2192 Appraisal Completed \u2192 Quoted \u2192 Quote Sent \u2192 Accepted \u2192 Work Booked \u2192 Work In Progress \u2192 Completed \u2192 Invoiced \u2192 Paid

\u2022 Appraisal Required \u2014 Initial state. Client needs an appraisal but no date assigned yet.
\u2022 Appraisal Booked \u2014 Requires scheduling: date, time, and appraiser (defaults to Owner contractor).

DATA FLOW DIAGRAM:
[Clients] \u2192 count \u2192 Dashboard KPI
[Appraisals] \u2192 count by status \u2192 Pipeline Breakdown
[Appraisals] \u2192 SUM(contract_amount) \u2192 Total Contract Value
[Leads] \u2192 count(open) \u2192 Open Leads KPI
[Appraisals] \u2192 sort by created_at DESC, limit 10 \u2192 Recent Appraisals List`,
    relatedPages: ['/clients', '/appraisals', '/leads', '/financials'],
  },

  '/clients': {
    title: 'Clients',
    summary: 'The Clients page manages all customer records. Each client has contact details, linked properties, and associated appraisals. Clients are the starting point for all CRM workflows.',
    howTo: [
      'Click "+ New Client" to add a client with name, email, phone, and address.',
      'Use the search bar to find clients by name, email, or phone.',
      'Click any client row to view their full profile, properties, and appraisals.',
      'Edit or delete clients from the client detail page.',
      'Clients can also be created inline when creating a standalone invoice.',
    ],
    detailed: `DATA MODEL \u2014 clients collection:
\u2022 name_first, name_last \u2014 Client name
\u2022 email, phone_mobile, phone_home \u2014 Contact info
\u2022 address_num, address_street, address_suburb, address_city \u2014 Postal address
\u2022 access_id \u2014 Legacy ID from Access database migration
\u2022 created_at \u2014 Timestamp

RELATIONSHIPS:
[Client] 1\u2192N [Properties] via properties.client_id
[Client] 1\u2192N [Appraisals] via appraisals.client_id
[Client] 1\u2192N [Invoices] via invoices.client_id
[Client] 1\u2192N [Insurance Claims] via insurance_claims.client_id

SEARCH: Indexes on (name_first, name_last), email, access_id, created_at.
Searches name_first, name_last, email, phone fields with case-insensitive regex.`,
    relatedPages: ['/properties', '/appraisals', '/invoices'],
  },

  '/properties': {
    title: 'Properties',
    summary: 'Properties represent physical addresses where roofing work is assessed or performed. Each property belongs to a client and can have multiple appraisals, roof details, and photos.',
    howTo: [
      'Properties are typically created from within a Client profile.',
      'Each property has address fields (number, street, suburb, city).',
      'Click a property to view its details, linked appraisals, and roof information.',
      'Add roof details (type, brand, pitch, area) on the property detail page \u2014 these feed into quote estimates.',
    ],
    detailed: `DATA MODEL \u2014 properties collection:
\u2022 client_id \u2014 Links to parent client
\u2022 property_address_num, _street, _suburb, _city \u2014 Full address
\u2022 access_id \u2014 Legacy migration ID

DATA MODEL \u2014 roof_details collection:
\u2022 property_id \u2014 Links to property
\u2022 roof_type \u2014 e.g., "Concrete Tile", "Iron/Steel"
\u2022 roof_brand \u2014 Manufacturer
\u2022 roof_pitch \u2014 Angle in degrees
\u2022 roof_area \u2014 Area in square metres (used in estimate calculations)

ESTIMATE FORMULA (feeds from Estimate Rates page):
  Restoration Estimate = roof_area \u00d7 restoration_rate_per_sqm
  Re-Roof Estimate = roof_area \u00d7 reroof_rate_per_sqm

DATA FLOW:
[Property] \u2192 roof_area \u2192 [Estimate Rates] \u00d7 rate \u2192 Appraisal Report Estimates
[Property] \u2192 address \u2192 appears on Quotes, Invoices, PDF Reports`,
    relatedPages: ['/clients', '/appraisals'],
  },

  '/appraisals': {
    title: 'Appraisals',
    summary: 'Appraisals are the core of the CRM \u2014 each represents a roofing job from initial assessment through to completion. They contain quote items, component checklists, conditions, and generate PDF quotes and reports.',
    howTo: [
      'Click "+ New Appraisal" to create one linked to a client and property.',
      'Set the quote type (e.g., Remedial Repairs, Full Restoration) and default roof type.',
      'The appraisal list can be filtered by status and searched. Respects CRM Start Date.',
      'From the detail page: add/edit quote items, assess components, view conditions.',
      'Generate PDFs: Quotation, Appraisal Report, Materials Breakdown, Profit Analysis.',
      'Create invoices from accepted appraisals using the "Generate Invoice" button.',
    ],
    detailed: `STATUS FLOW:
Appraisal Required \u2192 Appraisal Booked \u2192 Appraisal Completed \u2192 Quoted \u2192 Quote Sent \u2192 Accepted \u2192 Work Booked \u2192 Work In Progress \u2192 Completed \u2192 Invoiced \u2192 Paid

BOOKING AN APPRAISAL:
When moving from "Appraisal Required" to "Appraisal Booked", a modal requires:
\u2022 Scheduled date (required) \u2014 when the appraisal will take place
\u2022 Time (optional) \u2014 appointment time
\u2022 Appraiser (required) \u2014 selected from contractor list, defaults to Owner contractor
The booked date and appraiser are stored on the appraisal and displayed in Job Schedule.

DATA MODEL \u2014 appraisals collection:
\u2022 client_id, property_id \u2014 Links to client & property
\u2022 appraisal_date \u2014 Original date of assessment
\u2022 appraisal_booked_date \u2014 Scheduled date for the appraisal
\u2022 appraiser_id, appraiser_name \u2014 Contractor assigned to appraise
\u2022 appraisal_status \u2014 Current status in the workflow
\u2022 quote_type_ref \u2014 e.g., "Remedial Repairs", "Full Restoration"
\u2022 contract_amount \u2014 Total quote value = SUM(quote_items.total)
\u2022 appraisal_by \u2014 Assessor name
\u2022 created_at \u2014 Creation timestamp

QUOTE ITEM CALCULATIONS (quote_items collection):
Each line item stores:
\u2022 ref \u2014 Reference number
\u2022 description \u2014 Work description
\u2022 quantity \u2014 Number of units
\u2022 materials_rate \u2014 Material cost per unit ($)
\u2022 labour_rate \u2014 Labour cost per unit ($)
\u2022 total = quantity \u00d7 (materials_rate + labour_rate)
\u2022 materials = quantity \u00d7 materials_rate
\u2022 labour = quantity \u00d7 labour_rate

BUY COST FORMULA (for profit analysis):
Priority 1: If margin_percent set on item:
  buy_cost = materials \u00d7 (1 - margin_percent / 100)
Priority 2: If buy_cost_rate and materials_rate both exist on catalog product:
  buy_cost = materials \u00d7 (buy_cost_rate / materials_rate)
Priority 3: Default margin from Report Setup:
  buy_cost = materials \u00d7 (1 - default_materials_margin / 100)

PROFIT FORMULA:
  Materials Profit = Total Materials - Total Buy Cost
  Total Revenue = SUM(all item totals)
  contract_amount = Total Revenue

GST ON QUOTES:
  GST Rate = rate effective on appraisal_date (from gst_rates collection)
  GST Amount = contract_amount \u00d7 (gst_rate / 100)
  Total incl GST = contract_amount + GST Amount

COMPONENT ASSESSMENT (appraisal_components collection):
\u2022 appraisal_id \u2014 Links to appraisal
\u2022 component_type \u2014 e.g., "Ridge Capping", "Gutters"
\u2022 condition \u2014 Selected from pre-defined conditions (Roof Conditions page)
\u2022 notes \u2014 Additional observations

PDF REPORTS (5 types via ReportLab):
1. Quotation \u2014 Customer-facing, shows items + GST total
2. Appraisal Report \u2014 Full assessment with conditions, components, intro text
3. Materials Breakdown \u2014 Internal, shows material costs per item
4. Profit Analysis \u2014 Internal, shows buy costs vs sell prices, margin analysis
5. Invoice \u2014 Generated when status reaches Invoiced

JOB TASKS (job_tasks collection \u2014 visible in "Job Tasks" tab):
When a quote is Accepted, tasks can be auto-created from quote items or added manually.
Each task links to: appraisal, quote item(s), contractor, schedule.

TASK STATUS FLOW:
Draft \u2192 Offered \u2192 Accepted (by contractor) \u2192 Scheduled \u2192 In Progress \u2192 Completed

TASK PRICING:
\u2022 rate \u2014 Per-unit cost (auto-fills from contractor rate card or task catalog)
\u2022 unit \u2014 m2, lm, hour, each, fixed
\u2022 quantity \u2014 Amount of work
\u2022 total_cost = rate \u00d7 quantity

COST TRACKING:
Task Cost Summary compares total_task_cost vs quoted_contractor_cost.
Positive variance = over budget. Negative = under budget.
When ALL tasks are completed, system prompts to mark the job "Completed".`,
    relatedPages: ['/clients', '/properties', '/invoices', '/materials', '/report-setup', '/roof-conditions'],
  },

  '/leads': {
    title: 'Leads',
    summary: 'Leads track potential business opportunities before they become formal appraisals. Track lead source, status, and conversion.',
    howTo: [
      'Create new leads with contact information and source.',
      'Track lead status and follow-up dates.',
      'Convert leads to clients/appraisals when they progress.',
      'Filter by open/closed status.',
    ],
    detailed: `DATA MODEL \u2014 leads collection:
\u2022 Contact details (name, phone, email, address)
\u2022 lead_source \u2014 Where the lead came from
\u2022 lead_status \u2014 Current status
\u2022 lead_closed \u2014 Boolean flag
\u2022 follow_up_date \u2014 Next action date

CONVERSION FLOW:
[Lead] \u2192 Convert \u2192 [Client] + [Property] + [Appraisal]
Open leads (lead_closed \u2260 true) count appears on Dashboard.`,
    relatedPages: ['/clients', '/appraisals', '/'],
  },

  '/invoices': {
    title: 'Invoices',
    summary: 'The Invoices page displays all invoices grouped by status: Created, Sent, and Paid. Invoices can be generated from accepted appraisals or created standalone for verbal agreements and ad-hoc work.',
    howTo: [
      'Click "+ New Invoice" to create a standalone invoice with client, property, and line items.',
      'For appraisal-based invoices, generate from the Appraisal Detail page.',
      'Change status using the dropdown: Created \u2192 Sent \u2192 Paid.',
      'Use date range filters (From/To) to narrow results.',
      'Click "Clear Filters" to reset. Click any invoice to view full details + PDF.',
      'Void an invoice from the detail page (cannot be undone).',
    ],
    detailed: `STATUS FLOW:
Created \u2192 Sent \u2192 Paid (also: Void \u2014 terminal state)

DATA MODEL \u2014 invoices collection:
\u2022 invoice_number \u2014 Sequential (INV-170426, INV-170427, ...)
\u2022 invoice_number_seq \u2014 Numeric sequence for sorting
\u2022 invoice_date \u2014 Date of creation
\u2022 due_date \u2014 Payment due date
\u2022 status \u2014 Created | Sent | Paid | Void
\u2022 status_history[] \u2014 Array of {status, date, by} for audit trail
\u2022 client_id, client_name, client_email, client_phone
\u2022 property_id, property_address
\u2022 quote_type \u2014 Type of work
\u2022 line_items[] \u2014 Array of {ref, item, description, quantity, total}
\u2022 gst_rate \u2014 GST % at time of creation

INVOICE CALCULATION FORMULAS:
  Subtotal (excl GST) = SUM(line_items[].total)
  GST Amount = Subtotal \u00d7 (gst_rate / 100)
  Total (incl GST) = Subtotal + GST Amount

Example: Subtotal=$1,000, GST Rate=15%
  GST = $1,000 \u00d7 0.15 = $150.00
  Total = $1,000 + $150 = $1,150.00

APPRAISAL-BASED INVOICE:
  Line items copied from appraisal quote_items
  Client/property auto-populated from appraisal
  Appraisal status set to "Invoiced"

STANDALONE INVOICE:
  Select existing client or create new inline
  Select/create property
  Add items manually or from Product Catalog

DATA FLOW:
[Appraisal] \u2192 Generate Invoice \u2192 [Invoice] \u2192 Status changes \u2192 [Financials]
[Invoice status=Paid] \u2192 GST Report (Output GST)
[Invoice status=Sent, age>60d] \u2192 Financials Overdue list

PAGE LAYOUT: 3 stacked sections (Created, Sent, Paid) with 10 items each, scrollable.`,
    relatedPages: ['/appraisals', '/financials', '/expenses', '/materials'],
  },

  '/expenses': {
    title: 'Expenses',
    summary: 'Track business expenses with GST handling. Upload receipt/invoice images for record-keeping. Expenses feed into the Financial Dashboard and GST Report as Input GST (tax credits).',
    howTo: [
      'Click "+ New Expense" to add an expense with date, amount, supplier, and category.',
      'Enter the amount including or excluding GST \u2014 the system calculates the other.',
      'Upload a receipt or invoice image (stored as base64).',
      'Filter by date range or search by supplier/description.',
    ],
    detailed: `DATA MODEL \u2014 expenses collection:
\u2022 expense_date \u2014 Date of expense
\u2022 amount_excl_gst \u2014 Amount before GST
\u2022 gst_amount \u2014 GST component
\u2022 amount_incl_gst \u2014 Total including GST
\u2022 supplier_name \u2014 Vendor name
\u2022 description \u2014 What was purchased
\u2022 category \u2014 Expense category
\u2022 receipt_url \u2014 Base64 image of receipt

EXPENSE GST FORMULAS:
  If entering amount INCLUDING GST:
    GST Amount = amount_incl_gst \u00d7 3 / 23   (NZ 15% GST formula)
    Amount Excl GST = amount_incl_gst - GST Amount

  If entering amount EXCLUDING GST:
    GST Amount = amount_excl_gst \u00d7 (gst_rate / 100)
    Amount Incl GST = amount_excl_gst + GST Amount

DATA FLOW:
[Expenses] \u2192 SUM(gst_amount) \u2192 Financials "Input GST"
[Expenses] \u2192 SUM(amount_excl_gst) \u2192 Financials "Total Expenses"
[Expenses] \u2192 GST Report period breakdown \u2192 "Input GST" column
Net GST = Output GST (from Paid Invoices) - Input GST (from Expenses)`,
    relatedPages: ['/financials', '/invoices', '/suppliers'],
  },

  '/financials': {
    title: 'Financials',
    summary: 'The Financial Dashboard provides a comprehensive view of business finances. The Overview tab shows invoice status breakdowns (Created/Sent/Paid), KPIs, GST summary based on paid invoices, monthly stacked trends, and overdue invoices. The GST Report tab provides period-based tax reporting with GST101A form generation.',
    howTo: [
      'Overview Tab: View Created/Sent/Paid invoice cards with Excl GST, GST, and Total breakdowns.',
      'Use the period selector (All Time, This Year, This Quarter, This Month) to filter all data.',
      'The stacked bar chart shows monthly invoices colored by status (green=Paid, amber=Sent, blue=Created).',
      'Overdue section lists invoices with status "Sent" older than 60 days.',
      'GST Report Tab: Set date range and filing frequency for IRD reporting.',
      'Click "Generate GST101A" to produce an official NZ IRD return form PDF for a period or the full year.',
    ],
    detailed: `IMPORTANT: All GST calculations use PAID invoices only.

OVERVIEW KPI FORMULAS:
\u2022 Total Invoiced = SUM(all non-void invoices.total_inc_gst)
\u2022 Created Total = SUM(invoices where status=Created .total_inc_gst)
\u2022 Sent Total = SUM(invoices where status=Sent .total_inc_gst)
\u2022 Paid Total = SUM(invoices where status=Paid .total_inc_gst)
\u2022 Expenses = SUM(expenses.amount_incl_gst)
\u2022 Net Profit = Paid Revenue (excl GST) - Expenses (excl GST)
    = SUM(paid invoices.subtotal) - SUM(expenses.amount_excl_gst)
\u2022 Pipeline = SUM(appraisals.contract_amount) where status \u2208 {Quoted, Quote Sent, Accepted, Work Booked, Work In Progress}

GST SUMMARY FORMULAS (based on Paid invoices only):
\u2022 Output GST = SUM(invoices where status=Paid .gst_amount)
\u2022 Input GST = SUM(expenses.gst_amount)
\u2022 Net GST to Pay = Output GST - Input GST
    If negative \u2192 IRD owes you a refund
    If positive \u2192 you owe IRD
\u2022 Outstanding GST = SUM(invoices where status \u2208 {Created,Sent} .gst_amount)
    This is GST on unpaid invoices \u2014 not yet payable to IRD

OVERDUE INVOICES:
  Query: status = "Sent" AND invoice_date \u2264 (today - 60 days)
  Days Overdue = today - invoice_date

MONTHLY TREND CHART:
  Groups invoices by month (YYYY-MM) and status
  Stacked bars: bottom=Paid(green), middle=Sent(amber), top=Created(blue)

GST REPORT TAB \u2014 PERIOD BREAKDOWN:
  Filing periods determined by: gst_filing_frequency (1/2/6 months) + gst_period_start_month
  Per period shows: All Sales, Paid Sales, Output GST (paid only), Expenses, Input GST, Net GST
  Net GST per period = Paid GST in period - Expense GST in period

GST101A FORM (NZ IRD):
  Box 5 = Total Paid Sales incl GST
  Box 6 = Zero-rated supplies (currently 0)
  Box 7 = Box 5 - Box 6
  Box 8 = (Box 7 \u00d7 3) / 23    \u2190 IRD's GST extraction formula for 15%
  Box 9 = Adjustments (currently 0)
  Box 10 = Box 8 + Box 9 = Total Output Tax
  Box 11 = Total Expenses incl GST
  Box 12 = (Box 11 \u00d7 3) / 23   \u2190 Input tax extraction
  Box 13 = Credit adjustments (currently 0)
  Box 14 = Box 12 + Box 13 = Total Input Tax
  Box 15 = Box 10 - Box 14 = Net GST to Pay (or Refund if negative)

DATA FLOW DIAGRAM:
[Invoices status=Paid] \u2192 Output GST \u2192 GST Summary + GST Report
[Expenses] \u2192 Input GST \u2192 GST Summary + GST Report
[Both] \u2192 Net GST \u2192 GST101A Form (Box 15)
[Invoices status=Sent, age>60d] \u2192 Overdue List
[All non-void Invoices] \u2192 Monthly Trend (grouped by month+status)
[Report Settings] \u2192 gst_filing_frequency \u2192 Period boundaries`,
    relatedPages: ['/invoices', '/expenses', '/report-setup'],
  },

  '/calendar': {
    title: 'Calendar',
    summary: 'Visual calendar view of scheduled appraisals, work bookings, and follow-up dates. Helps manage the roofing work schedule.',
    howTo: [
      'View appointments and bookings on a monthly/weekly calendar.',
      'Click dates to see scheduled items.',
      'Appraisals with dates appear automatically based on their appraisal_date field.',
    ],
    detailed: `DATA SOURCE: appraisals collection
Displays appraisals as calendar events using appraisal_date.
Events are color-coded or labeled by appraisal_status.`,
    relatedPages: ['/appraisals', '/gantt'],
  },

  '/gantt': {
    title: 'Job Schedule',
    summary: 'The central work planning hub. Shows booked appraisals, scheduled job tasks on a Gantt timeline (colour-coded by contractor), unscheduled tasks with bulk scheduling, and GST due date markers.',
    howTo: [
      'Top section shows upcoming Appraisals Booked — click to view the appraisal.',
      'Main timeline shows scheduled tasks as coloured bars grouped by job.',
      'Bars are colour-coded by contractor (Owner = amber, each external gets a unique colour).',
      'Click a job header to expand/collapse its tasks.',
      'Click a task bar to navigate to the appraisal detail.',
      'Right panel shows unscheduled tasks. Pick a bulk date and click "Schedule All" to set all tasks in a job to the same day.',
      'Individually adjust task dates using the date picker on each unscheduled task.',
      'Red dots on the timeline mark GST return due dates.',
      'Use the week range selector (2/4/6/12 weeks) and navigation arrows to scroll the timeline.',
    ],
    detailed: `THREE SECTIONS:

1. APPRAISALS BOOKED (top):
Shows all appraisals with status "Appraisal Booked" and a booked date.
Displays: date, time, client, property, quote type, appraiser. Click row to navigate.
To book an appraisal: change status from "Appraisal Required" to "Appraisal Booked" \u2014 a modal requires date, time, and appraiser selection (defaults to Owner).

2. JOB TASKS TIMELINE (main area):
\u2022 Horizontal Gantt bars for each scheduled job task
\u2022 Grouped by job (appraisal) with expand/collapse
\u2022 Grey aggregate bar shows the overall span of all tasks in a job
\u2022 Colour-coded by contractor name
\u2022 Today line (orange), weekend shading, week markers
\u2022 GST due date markers (red dots) based on filing frequency from Report Settings

3. UNSCHEDULED PANEL (right side):
\u2022 Lists all tasks without a scheduled_start date (status not Completed/Declined)
\u2022 Grouped by job with client name and task count
\u2022 "Schedule All" button: pick a date, sets all tasks in that job to the same day, status \u2192 Scheduled
\u2022 Individual date picker per task for selective scheduling
\u2022 After bulk scheduling, selectively adjust dates as needed

BULK SCHEDULING:
POST /api/gantt/bulk-schedule sets scheduled_start and scheduled_end on multiple tasks.
Tasks in Draft or Accepted status are auto-moved to "Scheduled".

GST DUE DATES:
Based on gst_filing_frequency in report_settings:
\u2022 Monthly: 28th of every following month
\u2022 2-monthly: Feb 28, Apr 28, Jun 28, Aug 28, Oct 28, Dec 28
\u2022 6-monthly: Apr 28, Oct 28`,
    relatedPages: ['/appraisals', '/calendar', '/contractors', '/task-catalog'],
  },

  '/contractors': {
    title: 'Contractors',
    summary: 'Manage contractors with specialties and rate cards. Contractors are assigned to job tasks when quotes are accepted. Each contractor has agreed rates per task type (hourly, per m2, per linear metre, etc.).',
    howTo: [
      'Click "Add Contractor" to create a new contractor record.',
      'Set specialties (Roofer, Painter, Builder, Treatment Applicator, etc.).',
      'Add a Rate Card with per-task pricing (e.g. "Strip roof" = $18/m2).',
      'Set default hourly and m2 rates as fallbacks.',
      'Click a contractor card to expand and see their full rate card.',
      'When assigning tasks in Job Tasks tab, contractor rates auto-fill.',
    ],
    detailed: `DATA MODEL: contractors collection
\u2022 contractor_name, company_name, contact details
\u2022 specialties[] \u2014 Array of types: Roofer, Painter, Builder, Treatment Applicator, etc.
\u2022 default_hourly_rate \u2014 Fallback rate per hour ($)
\u2022 default_m2_rate \u2014 Fallback rate per m2 ($)
\u2022 rates[] \u2014 Rate card array: { task_name, rate, unit }
  e.g. { task_name: "Strip existing metal roof", rate: 18.00, unit: "m2" }

WHAT IS A RATE CARD?
A rate card is the agreed pricing between your business and a contractor for specific tasks.
It is the single source of truth for "what does this contractor charge for this work?"

Example \u2014 Smith Roofing rate card:
  Strip existing metal roof    \u2192 $18.00/m2
  Install longrun roofing      \u2192 $42.00/m2
  Install ridge capping        \u2192 $35.00/lm
  General labouring            \u2192 $55.00/hour

WHY RATE CARDS MATTER:
\u2022 Accurate quoting \u2014 when building quotes, you know exactly what the contractor will charge.
\u2022 Auto-fill pricing \u2014 when assigning job tasks, selecting a contractor fills the rate automatically.
\u2022 Consistency \u2014 task names come from the Task Catalog dropdown, so rate lookups always match.
\u2022 Rate changes \u2014 update the rate card once; all future quotes and tasks use the new rate.
  Existing quotes and tasks keep their locked-in price.

HOW TO SET UP A RATE CARD:
1. Edit a contractor and scroll to "Rate Card (per task)".
2. Click "Add Rate" to add a row.
3. Select a task from the dropdown (populated from Task Catalog).
   The task name, default rate, and unit auto-fill.
4. Adjust the rate to the agreed amount with this specific contractor.
5. Save. The rate card is now active.

OWNER / OPERATOR CONTRACTORS:
When the business owner also does hands-on work, mark them as "Owner / Operator" in the contractor record.
\u2022 Owner contractors are tracked and costed identically to external contractors for accurate job costing.
\u2022 Their rates appear in rate cards and auto-fill into job tasks like any other contractor.
\u2022 The key difference: owner labour cost is classified as RETAINED PROFIT, not a payable expense.
  No invoice is expected. The cost simply represents the owner's earned labour value.
\u2022 Job Task summary splits costs: Owner Labour (= profit) vs External Cost (= payable).
\u2022 This gives accurate job profitability: you see true cost to deliver the job even when the owner works on it.

RATE RESOLUTION (when assigning a job task):
1. Check contractor rate card for matching task_name
2. If no match, use default_hourly_rate with unit "hour"
3. Rate can always be manually overridden per task

INTEGRATION WITH JOB TASKS:
Contractors are assigned to tasks in the Appraisal Detail > Job Tasks tab.
Tasks track: Offered \u2192 Accepted \u2192 Scheduled \u2192 In Progress \u2192 Completed.
Calendar and Gantt chart show scheduled tasks by contractor.`,
    relatedPages: ['/appraisals', '/gantt', '/task-catalog', '/calendar'],
  },

  '/materials': {
    title: 'Product Catalog (Materials)',
    summary: 'The product catalog stores all materials and services used in quotes. Each product has rates, margins, supplier links, and roof type compatibility. Products feed into both appraisal quotes and standalone invoices.',
    howTo: [
      'Add products with name, category, supplier, buy cost rate, materials rate, and margin %.',
      'Products are available in the quote item builder when creating appraisals.',
      'Products also appear in standalone invoice creation (search/dropdown).',
      'Set per-product margin percentages to control internal pricing.',
      'Filter by category or supplier.',
    ],
    detailed: `DATA MODEL \u2014 product_catalog collection:
\u2022 name \u2014 Product name
\u2022 category \u2014 Product category
\u2022 supplier \u2014 Supplier name (links to suppliers collection)
\u2022 buy_cost_rate \u2014 What you pay per unit ($)
\u2022 materials_rate \u2014 What you charge per unit ($)
\u2022 labour_rate \u2014 Labour cost per unit ($)
\u2022 margin_percent \u2014 Markup percentage

PRICING FORMULAS:
  Sell Price per unit = materials_rate + labour_rate
  Line Item Total = quantity \u00d7 (materials_rate + labour_rate)

BUY COST CALCULATION (3-tier priority):
  1. margin_percent on product: buy_cost = materials \u00d7 (1 - margin_percent/100)
  2. Catalog ratio: buy_cost = materials \u00d7 (buy_cost_rate / materials_rate)
  3. Default margin: buy_cost = materials \u00d7 (1 - default_materials_margin/100)

  Example: materials_rate=$100, margin_percent=20%
    buy_cost = $100 \u00d7 (1 - 20/100) = $100 \u00d7 0.80 = $80.00
    Profit per unit = $100 - $80 = $20.00

ROOF TYPE FILTERING:
Products link to roof types via product_roof_types mapping.
When building a quote for "Concrete Tile" roof, only compatible products show.

DATA FLOW:
[Product Catalog] \u2192 Quote Builder dropdown \u2192 [Quote Items] \u2192 [Appraisal]
[Product Catalog] \u2192 Invoice Item picker \u2192 [Invoice Line Items]
[Product Catalog] + [buy_cost calc] \u2192 Profit Analysis PDF`,
    relatedPages: ['/suppliers', '/appraisals', '/product-roof-types', '/invoices'],
  },

  '/suppliers': {
    title: 'Suppliers',
    summary: 'Manage supplier records for materials and services. Suppliers are linked to products in the catalog and can be referenced on expenses.',
    howTo: [
      'Add suppliers with name, contact details, and account information.',
      'Link suppliers to products in the Product Catalog.',
      'Reference suppliers when recording expenses.',
    ],
    detailed: `DATA MODEL \u2014 suppliers collection:
\u2022 name (unique) \u2014 Supplier name
\u2022 Contact details (phone, email, address)
\u2022 Account/payment information

RELATIONSHIPS:
[Supplier] 1\u2192N [Products] via product_catalog.supplier field
[Supplier name] referenced in expenses.supplier_name`,
    relatedPages: ['/materials', '/expenses'],
  },

  '/inventory': {
    title: 'Inventory',
    summary: 'Track physical stock levels of materials. Monitor quantities, receive stock, and record usage against jobs.',
    howTo: [
      'Add inventory items with name, category, and initial quantity.',
      'Record stock movements: received (in), used (out), adjusted.',
      'Monitor low stock levels.',
    ],
    detailed: `DATA MODEL \u2014 inventory collection:
\u2022 name, category \u2014 Item identification
\u2022 quantity \u2014 Current stock level

Stock movements (stock_movements collection):
\u2022 inventory_item_id \u2014 Links to inventory item
\u2022 type \u2014 "in" | "out" | "adjust"
\u2022 quantity \u2014 Amount changed
\u2022 date, notes \u2014 When and why`,
    relatedPages: ['/materials', '/suppliers'],
  },

  '/insurance': {
    title: 'Insurance Claims',
    summary: 'Track insurance claims associated with appraisals. Record claim details, status, and insurer information for jobs involving insurance work.',
    howTo: [
      'Create insurance claims linked to specific appraisals.',
      'Track claim numbers, insurer details, and claim status.',
      'Record claim amounts and outcomes.',
    ],
    detailed: `DATA MODEL \u2014 insurance_claims collection:
\u2022 appraisal_id \u2014 Links to appraisal
\u2022 client_id \u2014 Links to client
\u2022 claim_number, insurer, status
\u2022 Financial details (claim amount, excess, etc.)`,
    relatedPages: ['/appraisals', '/clients'],
  },

  '/telemarketing': {
    title: 'Telemarketing Workspace',
    summary: 'Purpose-built call workspace for telemarketers. Log calls rapidly, access scripts with objection rebuttals, and book appraisals directly from successful calls with one click.',
    howTo: [
      'Call Workspace: Enter contact details in the quick-entry form. Click an outcome button to log instantly.',
      '"BOOK APPRAISAL" opens the booking panel \u2014 set date, time, appraiser. Click to create client + property + booked appraisal in one step.',
      'Scripts panel (right side): Click a script to read. Click "Objections" to flip to rebuttal cards.',
      'Call History: Full searchable log of all calls with outcomes and booking status.',
      'Scripts mode: View, edit, and create call scripts with objection/rebuttal pairs.',
      'Telemarketers are contractors with "Telemarketer" specialty \u2014 manage in the Contractors page.',
    ],
    detailed: `THREE MODES:
1. Call Workspace \u2014 Optimised for speed during live calls:
   \u2022 Quick-entry form: name, phone, address, suburb, city (dropdown), roof type (dropdown)
   \u2022 One-click outcome buttons: Appointment Booked, Follow-Up, Not Interested, No Answer, etc.
   \u2022 Booking panel: creates Client + Property + Appraisal (status "Appraisal Booked") in one step
   \u2022 Scripts sidebar: read scripts while on the call, flip to objection rebuttals instantly

2. Call History \u2014 Full searchable table of all logged calls

3. Scripts \u2014 Manage call scripts:
   \u2022 Types: Cold Call, Follow-Up, Closing, Special Offer
   \u2022 Each script has content, tips/hints, and objection/rebuttal pairs
   \u2022 Pre-loaded scripts: Opening Cold Call, Follow-Up, Storm Urgency, Neighbourhood Activity, Closing

QUICK BOOKING FLOW:
1. Telemarketer fills contact details during the call
2. Clicks "BOOK APPRAISAL" \u2192 booking panel opens
3. Sets date + time + appraiser (defaults to Owner)
4. Clicks "Create Client + Book Appraisal"
5. System creates: new Client, new Property, new Appraisal in "Appraisal Booked" status
6. Appraisal appears in Job Schedule immediately

TELEMARKETERS:
Telemarketers are contractors with the "Telemarketer" specialty.
Add a telemarketer: Contractors page \u2192 Add Contractor \u2192 check "Telemarketer" specialty.
They appear in the telemarketer dropdown for call assignment.`,
    relatedPages: ['/leads', '/clients', '/contractors', '/gantt'],
  },

  '/letters': {
    title: 'Letter Templates',
    summary: 'Manage letter/document templates for client communications. Create reusable templates with dynamic placeholders.',
    howTo: [
      'Create letter templates with title and body content.',
      'Use placeholders: {client_name}, {property_address}, {roof_type}, etc.',
      'Generate letters from appraisal or client contexts.',
    ],
    detailed: `Templates support placeholders that get replaced with actual data:
\u2022 {client_name} \u2192 Client full name
\u2022 {property_address} \u2192 Property street address
\u2022 {roof_type} \u2192 Roof type from property/appraisal
\u2022 {roof_brand} \u2192 Roof brand name`,
    relatedPages: ['/clients', '/appraisals'],
  },

  '/descriptions': {
    title: 'Descriptions Editor',
    summary: 'Configure reusable text descriptions for quote items. These descriptions auto-populate when building quotes, ensuring consistency across all appraisals.',
    howTo: [
      'Add descriptions by category (e.g., repair descriptions, material descriptions).',
      'These populate the description dropdown when adding quote items.',
      'Edit or remove descriptions as service offerings change.',
    ],
    detailed: `Pre-configured text blocks that streamline quote creation.
When adding a quote item in an appraisal, users select from these descriptions
instead of typing manually, ensuring consistent language across all quotes.

DATA FLOW:
[Descriptions] \u2192 Dropdown in Quote Item form \u2192 [Quote Item description field]`,
    relatedPages: ['/appraisals', '/item-requirements'],
  },

  '/item-requirements': {
    title: 'Item Requirements',
    summary: 'Define standard requirements for quote item types. These appear as default requirements when specific items are added to quotes.',
    howTo: [
      'Configure requirements per item type.',
      'Requirements auto-attach when matching items are added to quotes.',
    ],
    detailed: `Templates that automatically populate the requirements field for quote items
of matching types. Ensures compliance and consistency across all quotes.`,
    relatedPages: ['/appraisals', '/descriptions'],
  },

  '/roof-type-materials': {
    title: 'Roof Type Materials',
    summary: 'Map which materials/products are applicable to each roof type. This controls which products appear when quoting for specific roof types.',
    howTo: [
      'Select a roof type and assign compatible materials/products.',
      'This filtering ensures only relevant products show in the quote builder.',
    ],
    detailed: `Many-to-many mapping: roof_type \u2194 product_catalog
When an appraisal specifies roof_type = "Concrete Tile", the quote builder
only shows products mapped to that roof type.

This is the roof-type-centric view. See Product Roof Types for the product-centric view.`,
    relatedPages: ['/materials', '/product-roof-types', '/appraisals'],
  },

  '/product-roof-types': {
    title: 'Product Roof Types',
    summary: 'View and manage which roof types each product is compatible with. The product-centric inverse view of Roof Type Materials.',
    howTo: [
      'Select a product to see/edit which roof types it applies to.',
      'Toggle roof type compatibility on/off per product.',
    ],
    detailed: `Product-centric view of the roof type \u2194 product mapping.
Complementary to the Roof Type Materials page.`,
    relatedPages: ['/materials', '/roof-type-materials'],
  },

  '/dropdown-manager': {
    title: 'Dropdown Options',
    summary: 'Manage all configurable dropdown values used throughout the CRM \u2014 roof types, quote types, statuses, categories, component types, and other lists.',
    howTo: [
      'Select a dropdown category to view its options.',
      'Add, edit, or reorder options.',
      'Changes affect all forms and filters across the entire CRM immediately.',
    ],
    detailed: `DATA MODEL \u2014 dropdown_options collection:
Grouped by category. Key categories:
\u2022 roof_type \u2014 Used in properties, appraisals, materials mapping
\u2022 quote_type \u2014 Used in appraisals, report templates
\u2022 appraisal_status \u2014 Used in appraisal workflow
\u2022 component_type \u2014 Used in component assessments
\u2022 expense_category \u2014 Used in expenses

IMPACT: Changing dropdown values affects all forms and filters system-wide.
Removing a value does not delete existing data that references it.`,
    relatedPages: ['/appraisals', '/report-setup', '/roof-conditions'],
  },

  '/report-setup': {
    title: 'Report Setup',
    summary: 'Configure all PDF report generation settings. Includes company branding, introduction text, restoration/repair process steps, report templates, GST settings (number, filing frequency, rate history), invoice payment details, and the CRM Start Date.',
    howTo: [
      'Company & Logo: Set company name, logo, contact details. Set CRM Start Date to ignore old data.',
      'Introduction Text: Customise intro paragraphs with placeholders for appraisal reports.',
      'Restoration/Repairs: Configure step-by-step text for different roof types.',
      'Templates: Create per-quote-type templates to control which PDF sections appear.',
      'GST Settings: Set GST number, basis (Invoice/Payments), filing frequency (1/2/6 monthly), period start month, and manage rate history.',
      'Invoice Settings: Configure bank details, account name/number, payment terms.',
    ],
    detailed: `DATA MODEL \u2014 report_settings collection (single document):
\u2022 company_name, logo_url, phone, email, website
\u2022 representative, after_hours_phone, tagline
\u2022 default_materials_margin \u2014 Used in buy cost calculation (see Materials)
\u2022 crm_start_date \u2014 All transactional data before this date is hidden
\u2022 gst_number \u2014 IRD GST registration number
\u2022 gst_basis \u2014 "invoice" or "payments"
\u2022 gst_filing_frequency \u2014 1 (monthly), 2 (2-monthly), or 6 (6-monthly)
\u2022 gst_period_start_month \u2014 First month of filing cycle (1-12)
\u2022 Payment details: payment_bank_name, payment_account_name, payment_account_number, payment_terms, payment_instructions
\u2022 intro text fields, restoration steps, repair options, footer text

CRM START DATE:
When set, filters these collections by date:
\u2022 appraisals.appraisal_date \u2265 crm_start_date
\u2022 invoices.invoice_date \u2265 crm_start_date
\u2022 expenses.expense_date \u2265 crm_start_date
Does NOT filter: clients, properties, suppliers, products, dropdowns, templates, rates

GST RATE HISTORY (gst_rates collection):
\u2022 rate \u2014 GST percentage (e.g., 15)
\u2022 effective_from \u2014 Date rate takes effect
\u2022 description \u2014 Reason for change
System applies the rate active on appraisal_date for each quote.

REPORT TEMPLATES (report_templates collection):
\u2022 quote_type \u2014 Which quote type this template applies to
\u2022 sections[] \u2014 Which PDF sections to include
\u2022 intro_override \u2014 Custom intro text (overrides default)
\u2022 show_restoration_estimate, show_reroof_estimate \u2014 Toggles`,
    relatedPages: ['/appraisals', '/invoices', '/financials'],
  },

  // /estimate-rates retired 2026-02 — content merged into Estimate Templates.

  '/roof-conditions': {
    title: 'Roof Conditions',
    summary: 'Define standard condition descriptions for each roof component type. These populate condition dropdowns during appraisal assessments, ensuring consistent language across all inspections.',
    howTo: [
      'Select a roof type and component type.',
      'Add condition descriptions (e.g., "Good condition", "Moderate wear", "Requires replacement").',
      'These appear as dropdown options when assessing components in appraisals.',
    ],
    detailed: `DATA MODEL \u2014 roof_conditions collection:
\u2022 roof_type \u2014 e.g., "Concrete Tile"
\u2022 component_type \u2014 e.g., "Ridge Capping", "Gutters", "Downpipes"
\u2022 conditions[] \u2014 Array of condition description strings

Stored per roof_type + component_type combination.
During appraisal component assessment, user selects from these
pre-defined conditions rather than typing free text.`,
    relatedPages: ['/appraisals', '/dropdown-manager'],
  },

  '/audit-log': {
    title: 'Audit Log',
    summary: 'View a chronological log of all significant actions performed in the CRM. Admin-only access for compliance and troubleshooting.',
    howTo: [
      'Browse the log to see who did what and when.',
      'Filter by entity type or date range.',
      'Each entry shows the user, action, entity, and timestamp.',
    ],
    detailed: `DATA MODEL \u2014 audit_log collection:
\u2022 entity_type \u2014 e.g., "appraisal", "invoice", "client"
\u2022 entity_id \u2014 ID of affected record
\u2022 action \u2014 What was done (create, update, delete, status_change)
\u2022 user \u2014 Who performed the action
\u2022 created_at \u2014 When it happened
Indexed on (entity_type, entity_id) and created_at.`,
    relatedPages: ['/users'],
  },

  '/users': {
    title: 'User Management',
    summary: 'Manage CRM user accounts, roles, and access. Admin-only page. Controls who can log in and what they can see.',
    howTo: [
      'View all user accounts.',
      'Create new users with email, password, name, and role.',
      'Set user roles: Admin (full access) or User (standard access).',
      'Admin-only pages: Audit Log, Bulk Ops, Settings, Users, Data Sync, Migration, User Manual.',
    ],
    detailed: `DATA MODEL \u2014 users collection:
\u2022 email (unique index) \u2014 Login identifier
\u2022 password \u2014 bcrypt hashed
\u2022 name \u2014 Display name
\u2022 role \u2014 "admin" or "user"

AUTHENTICATION:
\u2022 JWT tokens stored in HTTP-only cookies (access_token + refresh_token)
\u2022 Access token expires in 1 hour, refresh token in 7 days
\u2022 Admin account seeded on startup`,
    relatedPages: ['/audit-log', '/settings'],
  },

  '/settings': {
    title: 'Company Settings',
    summary: 'General company configuration settings. Admin-only access. Most report and invoice settings are managed in Report Setup.',
    howTo: [
      'Configure company-wide settings.',
      'For report branding, GST, and invoice settings, use the Report Setup page instead.',
    ],
    detailed: `General settings page for company-wide configuration.
Most operational settings are in Report Setup (company details, GST, invoicing).`,
    relatedPages: ['/report-setup', '/users'],
  },
  '/task-catalog': {
    title: 'Task Catalog',
    summary: 'The Task Catalog is a reusable library of tasks associated with roof work. Tasks are used in job planning when quotes are accepted. Each task has a category, contractor type, default rate, and unit of measurement.',
    howTo: [
      'Click "Add Task" to create a new reusable task.',
      'Set category (Roofing, Painting, Treatment, etc.), contractor type, and default pricing.',
      'Tasks are grouped by category for easy browsing.',
      'Search and filter by category to find specific tasks.',
      'Tasks can be selected when creating Job Tasks for accepted quotes.',
    ],
    detailed: `DATA MODEL (task_catalog collection):
\u2022 name \u2014 Task name (e.g. "Strip existing roof", "Apply roof treatment")
\u2022 category \u2014 One of: Roofing, Painting, Building, Treatment, Plumbing, Electrical, Spouting, Flashing, Ridge, General
\u2022 contractor_type \u2014 Default specialty needed (e.g. Roofer, Painter, Treatment Applicator)
\u2022 default_unit \u2014 Pricing unit: m2, lm (linear metre), hour, each, fixed
\u2022 default_rate \u2014 Default rate per unit ($)

USAGE IN JOB PLANNING:
When creating job tasks (Appraisal Detail > Job Tasks tab), you can pick from the catalog.
The task name, category, rate, and unit auto-fill from the catalog entry.
Contractor rate cards can override these defaults.

FRANCHISE NOTE: Task catalog is shared with franchise copies during creation.`,
    relatedPages: ['/contractors', '/appraisals'],
  },
  '/franchise-manager': {
    title: 'Franchise Manager',
    summary: 'Create and manage franchise CRM copies. Each franchise gets a clean, independent CRM instance with shared configuration but zero client or job data from the master account.',
    howTo: [
      'Click "New Franchise" and provide business name, admin email, and password.',
      'The franchise admin logs in with those credentials and sets up their business info.',
      'Click a franchise card to expand and see their data counts.',
      '"View Data" enters read-only mode to see their data across all pages.',
      '"Suspend" blocks all franchise users from logging in.',
      '"Delete Permanently" removes the franchise and all its data.',
    ],
    detailed: `FRANCHISE ARCHITECTURE:
Each franchise gets their own isolated MongoDB database (rtr_franchise_{name}).
Shared config seeded: dropdowns, product catalog, suppliers, GST rates, estimate rates, roof conditions, task catalog.
NOT shared: clients, properties, appraisals, invoices, expenses, work sheets, leads, report settings.

FRANCHISE RESTRICTIONS:
\u2022 Cannot create further franchise copies
\u2022 No Data Sync or Access database export
\u2022 Cannot see master account data
\u2022 Cannot access Franchise Manager

MASTER ADMIN CAPABILITIES:
\u2022 View any franchise data (read-only)
\u2022 Suspend/reactivate access instantly
\u2022 Full financial visibility into franchise operations
\u2022 Delete franchise permanently`,
    relatedPages: ['/users', '/settings'],
  },

  '/roof-report': {
    title: 'Roof Report',
    summary: 'Build a component-by-component roof inspection report for any appraisal. Pick tiles/sheeting/ridging/valleys/barges/spouting/fastenings/flashings comment scripts, set defect quantities, and write Roof Condition + Roof Requirements narratives. Also embedded inside each Appraisal \u2192 Components \u2192 Roof Report tab so field staff never need to leave the appraisal.',
    howTo: [
      'Pick an appraisal (or open one and use Components \u2192 Roof Report).',
      'Filter Roof Type \u2014 \'Auto\' uses the property\'s roof type; override via dropdown.',
      'Click each component tab, toggle matching comment scripts, enter a Defect Qty.',
      'Use the Roof Condition / Roof Requirements narrative tabs: pick a template or write free text.',
      'Click Save Report to persist everything to the appraisal.',
    ],
    detailed: `COLLECTIONS USED:
\u2022 roof_report_comments \u2014 82 entries imported from legacy ConditionComment *Tbl (8 tables)
\u2022 appraisal_condition_comments (category=roof) \u2014 11 Roof Condition templates
\u2022 appraisal_condition_comments (category=roof_requirements) \u2014 19 Roof Requirements templates
\u2022 roof_reports \u2014 per-appraisal { sections, defect_qty, roof_condition, roof_requirements, notes }

ROOF-TYPE FILTER: canonicalizes values like \"Modern Concrete\" \u2192 \"Concrete Tile\" and includes empty roof_type entries (universal) in every filter.

LEGACY SEED: AppraisalTbl.Defect*Qty seeded 11 defect-qty records; AppraisalTbl.RoofCondition + RoofRequirements seeded 149 + 22 narratives. Non-destructive merge on rerun.`,
    relatedPages: ['/appraisals', '/appraisal-conditions', '/component-rates'],
  },

  '/component-rates': {
    title: 'Component Rate Tables',
    summary: 'Pricing engine behind the Quote Calculation tab. Stores Type and Profile rows per component (Sheet, Tile, Ridge, Flashing, Barge, Valley, Spouting, Fastening, Misc \u2026) with Buy / Materials / Labour / Contractor rates. Imported from legacy MS Access.',
    howTo: [
      'Pick a component on the left.',
      'Click a row to edit Buy $, Materials $, Labour $, Contractor $.',
      'Add rows for new products \u2014 match the name to the Appraisal Components dropdowns so rates auto-apply.',
    ],
    detailed: `COLLECTION \u2014 component_rate_tables:
\u2022 component \u2014 sheet/tile/ridge/flashing/barge/valley/spouting/fastening/misc
\u2022 kind \u2014 type | profile
\u2022 name \u2014 Type or Profile name used for matching
\u2022 buy_cost / materials_rate / labour_rate / contractor_rate
\u2022 profile-only extras (SheetCover for ceil(face/cover) auto-calc, etc.)

Imported 1,615 rows total (1,478 type rates + 137 profile rows).
Referenced by core/quote_calc.py to compute per-line and per-appraisal totals.`,
    relatedPages: ['/appraisals', '/suppliers'],
  },

  '/appraisal-conditions': {
    title: 'Appraisal Conditions',
    summary: 'Library of reusable narrative templates grouped into four categories: Appraisal Condition, Roof Condition, Roof Requirements, Extra Work. Drives dropdowns on the Roof Report page and the Quote Comments panel inside Appraisal Components.',
    howTo: [
      'Pick a category tab at the top.',
      'Use the roof-type chips to filter; leave blank for universal entries.',
      'Click a row to edit name / comment / recommendation / roof_type / effective_date.',
      'Click + New Entry for a new template.',
      'Deep-link via ?category=appraisal|roof|roof_requirements|extra_work.',
    ],
    detailed: `COLLECTION \u2014 appraisal_condition_comments:
\u2022 name, comment, recommendation, roof_type, order, category, estimate_amount, effective_date

Seeded from legacy tables:
\u2022 ConditionCommentQuotationAppraisalConditionTbl \u2192 category=appraisal (29)
\u2022 ConditionCommentRoofConditionTbl \u2192 category=roof (11)
\u2022 ConditionCommentExtraWorkCommentTbl \u2192 category=extra_work (5)
\u2022 CommentRoofRecomendationTbl \u2192 category=roof_requirements (19)

Roof types normalized to CRM dropdown values (Clay Tile, Concrete Tile, Long Run Iron, etc.); blanks kept blank.`,
    relatedPages: ['/roof-report', '/appraisals'],
  },

  '/orders': {
    title: 'Purchase Orders',
    summary: 'Track supplier material orders generated from accepted quotes. Grouped by appraisal \u2192 supplier, auto-filled from the Product Catalog, with PDF export and one-click email to the supplier.',
    howTo: [
      'Open an accepted appraisal and click \u2018Generate POs\u2019.',
      'Edit line quantities/prices on Draft POs. Add products from the supplier\'s catalog.',
      'Pick Deliver (Job Site / Office / Owner / Contractor) or Pickup.',
      'Click Send to Supplier to email the PO with a pre-filled message, or Mark Sent for phone/fax orders.',
      'When materials arrive, click Receive Items to record partial/full delivery.',
    ],
    detailed: `STATUS FLOW: Draft \u2192 Sent \u2192 Ordered \u2192 Partially Received \u2192 Received \u2192 Cancelled.

PDF: A4 ReportLab document with company header, supplier details, delivery section, line items, subtotal/GST/total, notes.

GENERATION RULES:
\u2022 Quote items matching a product_catalog row are included
\u2022 Items for the same supplier are grouped into one PO
\u2022 Non-catalog items (custom labour, etc.) are skipped and reported to the user`,
    relatedPages: ['/appraisals', '/suppliers', '/inventory'],
  },
};

export default MANUAL;
